import React, { useMemo } from "react";

import { formatNumber, useSoilSightCurrency, useTranslation } from "utils";

import { DETAIL_COLUMN_WIDTHS, DETAIL_TABLE_MIN_WIDTH_COLLAPSED, DETAIL_TABLE_MIN_WIDTH_EXPANDED } from "consts";

import { Button, Icon, Table, Tooltip } from "components";

import { RotationCycleMarker } from "pages/app/scenario-analysis/components/detailed-calculations/RotationCycleMarker";
import { DetailedCalculationRow, ScenarioCalculationData, formatPerHectare as money } from "pages/app/scenario-analysis/utils/detailedCalculations";

type DetailBreakdownTableProps = {
  data: ScenarioCalculationData;
  mainCropLabelLookup: Record<string, string>;
  otherExpanded: boolean;
  onToggleOther: (expanded: boolean) => void;
  dataTestId?: string;
};

type DetailRow = { key: number; row: DetailedCalculationRow; cumulative: number };

const asDetailRow = (record: object) => record as DetailRow;

const PREFIX = "detailedCalculations.";

/** Full read-only year-by-year breakdown. Every value is API-sourced — see detailedCalculations.ts. */
export const DetailBreakdownTable = ({
  data,
  mainCropLabelLookup,
  otherExpanded,
  onToggleOther,
  dataTestId = "detail-breakdown",
}: DetailBreakdownTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol } = useSoilSightCurrency();

  const { rows, cumulatives, totals } = data;

  const decimal = (value: number, decimalPlaces = 1) => formatNumber({ number: value, decimalPlaces });
  // A sowing year harvested nothing, so its yield is absent rather than 0.0 — which would read as a crop failure.
  const optionalDecimal = (value: number) => (value === null || value === undefined ? "—" : decimal(value));
  const perHa = `${currencySymbol}/ha`;
  const perTonne = `${currencySymbol}/t`;
  const hasIrrigation = rows.some((row) => row.irrigationCosts !== undefined);
  const minWidth = (otherExpanded ? DETAIL_TABLE_MIN_WIDTH_EXPANDED : DETAIL_TABLE_MIN_WIDTH_COLLAPSED) + (hasIrrigation ? DETAIL_COLUMN_WIDTHS.irrigation : 0);

  const tableRows = useMemo<DetailRow[]>(() => rows.map((row, index) => ({ key: row.year, row, cumulative: cumulatives[index] ?? 0 })), [rows, cumulatives]);

  const columnTitle = (labelKey: string, unit?: string, hint?: string) => {
    const label = t(`${PREFIX}column.${labelKey}`);
    return (
      <>
        <Tooltip title={hint || label}>
          <span className="detail-breakdown-col-label">{label}</span>
        </Tooltip>
        {unit ? <span className="detail-breakdown-col-unit">{unit}</span> : null}
      </>
    );
  };

  const otherToggleLabel = t(`${PREFIX}${otherExpanded ? "collapseOther" : "expandOther"}`);

  const otherToggle = (
    <Tooltip title={otherToggleLabel}>
      <Button
        type="text"
        className="detail-breakdown-other-toggle"
        icon={<Icon iconName={otherExpanded ? "tabler/circle-minus" : "tabler/circle-plus"} size="s" />}
        onClick={() => onToggleOther(!otherExpanded)}
        aria-label={otherToggleLabel}
        dataTestId={`${dataTestId}-other-toggle`}
      />
    </Tooltip>
  );

  const cropLabel = (row: DetailedCalculationRow) => (row.cropId ? mainCropLabelLookup[row.cropId] ?? "—" : "—");

  const valueColumn = (
    field: keyof DetailedCalculationRow,
    labelKey: string,
    unit: string | undefined,
    width: number,
    className: string,
    hint?: string,
    format: (value: number) => string = money
  ) => ({
    title: columnTitle(labelKey, unit, hint),
    key: field,
    width,
    className,
    render: (_: unknown, record: object) => format(asDetailRow(record).row[field] as number),
  });

  const toggleTitle = (labelKey: string) => {
    const label = t(`${PREFIX}column.${labelKey}`);
    return (
      <>
        <span className="detail-breakdown-col-toggle">
          {otherToggle}
          <Tooltip title={label}>
            <span className="detail-breakdown-col-label">{label}</span>
          </Tooltip>
        </span>
        <span className="detail-breakdown-col-unit">{perHa}</span>
      </>
    );
  };

  // The toggle rides on the first sub-column when expanded, so collapsing stays reachable.
  const otherColumns = otherExpanded
    ? [
        {
          title: toggleTitle("cleaning"),
          key: "cleaningCosts",
          width: DETAIL_COLUMN_WIDTHS.cleaning,
          className: "detail-breakdown-cost",
          render: (_: unknown, record: object) => money(asDetailRow(record).row.cleaningCosts),
        },
        valueColumn("dryingStorageCosts", "dryingStorage", perHa, DETAIL_COLUMN_WIDTHS.dryingStorage, "detail-breakdown-cost"),
        valueColumn("insuranceCosts", "insurance", perHa, DETAIL_COLUMN_WIDTHS.insurance, "detail-breakdown-cost"),
      ]
    : [
        {
          title: toggleTitle("other"),
          key: "otherCosts",
          width: DETAIL_COLUMN_WIDTHS.other,
          className: "detail-breakdown-cost",
          render: (_: unknown, record: object) => money(asDetailRow(record).row.otherCosts),
        },
      ];

  const columns = [
    {
      title: columnTitle("year"),
      key: "year",
      fixed: "left" as const,
      width: DETAIL_COLUMN_WIDTHS.year,
      className: "detail-breakdown-year-cell",
      render: (_: unknown, record: object) => {
        const { row } = asDetailRow(record);
        return (
          <span className="detail-breakdown-year-wrap">
            <span className={`detail-breakdown-year${row.isNewCycle ? " detail-breakdown-year-new-cycle" : ""}`}>{row.calendarYear}</span>
            {row.isNewCycle ? <RotationCycleMarker className="detail-breakdown-cycle-marker" /> : null}
          </span>
        );
      },
    },
    {
      title: columnTitle("crop"),
      key: "crop",
      fixed: "left" as const,
      width: DETAIL_COLUMN_WIDTHS.crop,
      className: "detail-breakdown-crop-cell",
      render: (_: unknown, record: object) => <span className="detail-breakdown-crop-label">{cropLabel(asDetailRow(record).row)}</span>,
    },
    {
      title: t(`${PREFIX}group.revenue`),
      key: "group-revenue",
      className: "detail-breakdown-group-revenue",
      children: [
        valueColumn("adjustedYield", "yield", "t/ha", DETAIL_COLUMN_WIDTHS.yield, "", undefined, optionalDecimal),
        valueColumn("sellingPrice", "sellingPrice", perTonne, DETAIL_COLUMN_WIDTHS.sellingPrice, "", undefined, (value) => decimal(value, 2)),
        valueColumn("strawRevenue", "strawRevenue", perHa, DETAIL_COLUMN_WIDTHS.strawRevenue, "", t(`${PREFIX}strawRevenueHint`)),
        valueColumn("revenueTotal", "revenueTotal", perHa, DETAIL_COLUMN_WIDTHS.revenueTotal, ""),
        valueColumn("lossesFodderPct", "lossesFodder", "%", DETAIL_COLUMN_WIDTHS.lossesFodder, "", undefined, (value) => decimal(value)),
        valueColumn("lossesMouldPct", "lossesMould", "%", DETAIL_COLUMN_WIDTHS.lossesMould, "", undefined, (value) => decimal(value)),
        valueColumn(
          "revenueAdjusted",
          "revenueAdjusted",
          perHa,
          DETAIL_COLUMN_WIDTHS.revenueAdjusted,
          "detail-breakdown-emphasis",
          t(`${PREFIX}revenueAdjustedHint`)
        ),
        valueColumn("subsidies", "subsidies", perHa, DETAIL_COLUMN_WIDTHS.subsidies, ""),
      ],
    },
    {
      title: t(`${PREFIX}group.costs`),
      key: "group-costs",
      className: "detail-breakdown-group-costs",
      children: [
        valueColumn("seedCostsMain", "seedCostsMain", perHa, DETAIL_COLUMN_WIDTHS.seedMain, "detail-breakdown-cost"),
        {
          title: columnTitle("seedCostsCover", perHa),
          key: "seedCostsCover",
          width: DETAIL_COLUMN_WIDTHS.seedCover,
          className: "detail-breakdown-cost",
          render: (_: unknown, record: object) => {
            const { row } = asDetailRow(record);
            return (
              <span className="detail-breakdown-cover-crop-cell">
                {money(row.seedCostsCover)}
                {row.coverCropMismatch ? (
                  <Tooltip title={t(`${PREFIX}coverCropNotSownHint`)}>
                    <Icon iconName="tabler/alert-triangle" size="s" className="detail-breakdown-cover-crop-mismatch-icon" />
                  </Tooltip>
                ) : null}
              </span>
            );
          },
        },
        valueColumn("fertiliserCosts", "fertiliser", perHa, DETAIL_COLUMN_WIDTHS.fertiliser, "detail-breakdown-cost"),
        valueColumn("plantProtectionCosts", "plantProtection", perHa, DETAIL_COLUMN_WIDTHS.plantProtection, "detail-breakdown-cost"),
        valueColumn("dieselCosts", "diesel", perHa, DETAIL_COLUMN_WIDTHS.diesel, "detail-breakdown-cost"),
        valueColumn("machineryCosts", "machinery", perHa, DETAIL_COLUMN_WIDTHS.machinery, "detail-breakdown-cost"),
        ...(hasIrrigation ? [valueColumn("irrigationCosts", "irrigation", perHa, DETAIL_COLUMN_WIDTHS.irrigation, "detail-breakdown-cost")] : []),
        ...otherColumns,
        valueColumn("totalCosts", "totalCosts", perHa, DETAIL_COLUMN_WIDTHS.totalCosts, "detail-breakdown-emphasis"),
      ],
    },
    {
      title: t(`${PREFIX}group.modelledImpact`),
      key: "group-impact",
      className: "detail-breakdown-group-impact",
      children: [
        valueColumn("yieldChangesImpact", "yieldChanges", perHa, DETAIL_COLUMN_WIDTHS.yieldChanges, "detail-breakdown-impact"),
        valueColumn("costSavingsImpact", "costSavings", perHa, DETAIL_COLUMN_WIDTHS.costSavings, "detail-breakdown-impact"),
        valueColumn("resilienceImpact", "resilienceImpact", perHa, DETAIL_COLUMN_WIDTHS.resilienceImpact, "detail-breakdown-impact"),
      ],
    },
    {
      title: t(`${PREFIX}group.results`),
      key: "group-results",
      className: "detail-breakdown-group-results",
      children: [
        valueColumn("cmMid", "cmMid", perHa, DETAIL_COLUMN_WIDTHS.cmMid, "detail-breakdown-cm"),
        {
          title: columnTitle("cmRange", perHa),
          key: "cmRange",
          width: DETAIL_COLUMN_WIDTHS.cmRange,
          className: "detail-breakdown-range",
          render: (_: unknown, record: object) => {
            const { row } = asDetailRow(record);
            return `${money(row.cmLow)} – ${money(row.cmHigh)}`;
          },
        },
        {
          title: columnTitle("cumulativeCm", perHa),
          key: "cumulativeCm",
          width: DETAIL_COLUMN_WIDTHS.cumulativeCm,
          className: "detail-breakdown-cumulative",
          render: (_: unknown, record: object) => money(asDetailRow(record).cumulative),
        },
      ],
    },
  ];

  const renderTotals = () => {
    const costCells = [
      totals.seedCostsMain,
      totals.seedCostsCover,
      totals.fertiliserCosts,
      totals.plantProtectionCosts,
      totals.dieselCosts,
      totals.machineryCosts,
      ...(hasIrrigation ? [totals.irrigationCosts] : []),
      ...(otherExpanded ? [totals.cleaningCosts, totals.dryingStorageCosts, totals.insuranceCosts] : [totals.otherCosts]),
    ];

    // Year and Crop are merged into the label cell, so the running index starts after both.
    let index = 1;
    const cell = (content: React.ReactNode, className?: string) => {
      index += 1;
      return (
        <Table.Summary.Cell key={index} index={index} className={className}>
          {content}
        </Table.Summary.Cell>
      );
    };

    return (
      <Table.Summary.Row className="detail-breakdown-total-row">
        <Table.Summary.Cell index={0} colSpan={2} className="detail-breakdown-total-label">
          {t(`${PREFIX}totalYears`, { count: rows.length })}
        </Table.Summary.Cell>
        {/* Yield, price and loss percentages have no meaningful sum; straw revenue is money, so it adds up. */}
        {[0, 1].map(() => cell(null))}
        {cell(money(totals.strawRevenue))}
        {[0, 1, 2].map(() => cell(null))}
        {cell(money(totals.revenueAdjusted), "detail-breakdown-emphasis")}
        {cell(money(totals.subsidies))}
        {costCells.map((value) => cell(money(value), "detail-breakdown-cost"))}
        {cell(money(totals.totalCosts), "detail-breakdown-emphasis")}
        {cell(money(totals.yieldChangesImpact), "detail-breakdown-impact")}
        {cell(money(totals.costSavingsImpact), "detail-breakdown-impact")}
        {cell(money(totals.resilienceImpact), "detail-breakdown-impact")}
        {cell(money(totals.cmMid), "detail-breakdown-cm")}
        {cell(`${money(totals.cmLow)} – ${money(totals.cmHigh)}`, "detail-breakdown-range")}
        {cell(money(totals.cmMid), "detail-breakdown-cumulative")}
      </Table.Summary.Row>
    );
  };

  return (
    <div className="detail-breakdown" data-testid={dataTestId}>
      <Table<DetailRow>
        className="detail-breakdown-table"
        data={tableRows}
        columns={columns}
        rowKey="key"
        pagination={false}
        scroll={{ x: minWidth }}
        rowClassName={(record) => (asDetailRow(record).row.isNewCycle ? "detail-breakdown-new-cycle-row" : "")}
        summary={renderTotals}
      />
    </div>
  );
};

export default DetailBreakdownTable;
